import { z } from 'zod'
import {
  TriageSuggestedStatusResponseSchema,
  type FindingAggregateResponseStatistics,
} from '../../utils/api-client/api-schemas.ts'
import type { FilterableClassification, FilterableFixStatus } from '../tables/filter-state-store.tsx'

/**
 * Which findings the panel counts and the table lists.
 *
 * Stored as exclusions rather than inclusions so that outcomes the backend adds later are in scope
 * by default — an inclusion list written today would silently drop them.
 */
export type ResolutionScope = {
  triageOutcomes: FilterableClassification[]
  fixOutcomes: FilterableFixStatus[]
  /** Severity labels come from the scan's own scale, so they are strings rather than an enum. */
  severityLabels: string[]
}

export const EMPTY_RESOLUTION_SCOPE: ResolutionScope = { triageOutcomes: [], fixOutcomes: [], severityLabels: [] }

export const ScopeGroupSchema = z.enum(['triageOutcomes', 'fixOutcomes', 'severityLabels'])
export type ScopeGroup = z.infer<typeof ScopeGroupSchema>

export const TRIAGE_OUTCOME_KEYS: FilterableClassification[] = [...TriageSuggestedStatusResponseSchema.options]

export const FIX_OUTCOME_KEYS: FilterableFixStatus[] = [
  'completed',
  'failed',
  'no-recommendations-available',
  'blocked',
  'excluded',
]

export const TRIAGE_OUTCOME_LABELS: Record<FilterableClassification, string> = {
  true_positive: 'True positive',
  suspicious: 'Likely true positive',
  inconclusive: 'Inconclusive',
  false_positive: 'False positive',
  wont_fix: "Won't fix",
  not_triaged: 'Not triaged',
  blocked: 'Blocked',
  excluded: 'Excluded',
  error: 'Error',
}

export const FIX_OUTCOME_LABELS: Record<FilterableFixStatus, string> = {
  completed: 'Available fix',
  failed: 'Error',
  'no-recommendations-available': 'No recommendations',
  blocked: 'Blocked',
  excluded: 'Excluded',
}

/**
 * How many findings sit behind each triage outcome.
 *
 * `suspicious` is the filter's name for the outcome the statistics call `likely_true_positive`, and
 * `error` has no count field at all — the aggregate response reports eight triage outcomes where the
 * filter enum has nine. Excluding Error therefore removes findings from the table without changing
 * any panel count, which is why it reads 0 here rather than being hidden: hiding it would leave no
 * way to exclude it.
 */
export const getTriageOutcomeCounts = (
  statistics: FindingAggregateResponseStatistics
): Record<FilterableClassification, number> => {
  const { outcome } = statistics.triage

  return {
    true_positive: outcome.true_positive,
    suspicious: outcome.likely_true_positive,
    inconclusive: outcome.inconclusive,
    false_positive: outcome.false_positive,
    wont_fix: outcome.wont_fix,
    not_triaged: outcome.not_triaged,
    blocked: outcome.blocked,
    excluded: outcome.excluded,
    error: 0,
  }
}

/** How many findings sit behind each fix outcome, across the status and outcome breakdowns. */
export const getFixOutcomeCounts = (
  statistics: FindingAggregateResponseStatistics
): Record<FilterableFixStatus, number> => {
  const { status, outcome } = statistics.fix

  return {
    completed: status.completed,
    failed: status.failed,
    'no-recommendations-available': status.no_recommendations,
    blocked: outcome.blocked,
    excluded: outcome.excluded,
  }
}

export const countScopeExclusions = (scope: ResolutionScope): number =>
  scope.triageOutcomes.length + scope.fixOutcomes.length + scope.severityLabels.length

export const isScopeEmpty = (scope: ResolutionScope): boolean => countScopeExclusions(scope) === 0

/** Adds a value to a scope group's exclusions, or removes it if it is already excluded. */
export const toggleScopeExclusion = ({
  scope,
  group,
  value,
}: {
  scope: ResolutionScope
  group: ScopeGroup
  value: string
}): ResolutionScope => {
  const excluded: string[] = scope[group]
  const next = excluded.includes(value) ? excluded.filter(entry => entry !== value) : [...excluded, value]

  // The three groups are typed by their own enums, so each is narrowed at its own key rather than
  // through a computed one — a computed key would widen all three to `string[]`.
  return group === 'triageOutcomes'
    ? { ...scope, triageOutcomes: TRIAGE_OUTCOME_KEYS.filter(key => next.includes(key)) }
    : group === 'fixOutcomes'
      ? { ...scope, fixOutcomes: FIX_OUTCOME_KEYS.filter(key => next.includes(key)) }
      : { ...scope, severityLabels: next }
}

/** The findings filters a scope produces, in the names `mapFilterStateToGetScanFindingsParams` uses. */
export type ScopeFilters = {
  triageSuggestedStatuses?: FilterableClassification[]
  fixStatuses?: FilterableFixStatus[]
  includeSeverityLabels?: string[]
}

/** No scope applied: every dimension unconstrained. */
export const EMPTY_SCOPE_FILTERS: ScopeFilters = {}

/**
 * Turns the scope's exclusions into the findings filters that express them.
 *
 * The API's filters are inclusion lists, so each group is inverted: excluding False positive sends
 * every other triage outcome. A group with nothing excluded sends `undefined` instead of the full
 * list, which keeps an unscoped panel making exactly the request it made before this control
 * existed — and leaves room for outcomes the backend adds later.
 *
 * Severity is the exception. Its labels come from the scan's scale rather than a fixed enum, so the
 * full set has to be supplied by the caller to invert against.
 */
export const toScopeFilters = ({
  scope,
  severityLabels,
}: {
  scope: ResolutionScope
  /** Every severity label on the scan's scale, in scale order. */
  severityLabels: string[]
}): ScopeFilters => {
  const excludedSeverities = new Set(scope.severityLabels)
  const includedSeverities = severityLabels.filter(label => !excludedSeverities.has(label))

  return {
    triageSuggestedStatuses:
      scope.triageOutcomes.length > 0
        ? TRIAGE_OUTCOME_KEYS.filter(key => !scope.triageOutcomes.includes(key))
        : undefined,
    fixStatuses:
      scope.fixOutcomes.length > 0 ? FIX_OUTCOME_KEYS.filter(key => !scope.fixOutcomes.includes(key)) : undefined,
    includeSeverityLabels: scope.severityLabels.length > 0 ? includedSeverities : undefined,
  }
}

/**
 * Narrows a set of already-selected values to those the scope still admits.
 *
 * An empty selection means "unconstrained", so it narrows to the scope's own list rather than
 * staying empty — otherwise a scoped panel with no filter of its own would send nothing and the
 * exclusions would have no effect.
 */
export const intersectWithScope = <TValue extends string>({
  selected,
  admitted,
}: {
  selected: TValue[] | undefined
  admitted: TValue[] | undefined
}): TValue[] | undefined => {
  if (!admitted) {
    return selected
  }
  if (!selected || selected.length === 0) {
    return admitted
  }

  return selected.filter(value => admitted.includes(value))
}
